import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  SimpleGrid,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { CloseIcon } from '@chakra-ui/icons';
import 'react-phone-input-2/lib/style.css';
import '../../../assets/css/PhoneInputCustom.css';
import CommonDatePicker from 'components/commonInput/CommonDatePicker';
import CommonInput from 'components/commonInput/CommonInput';
import PageLoader from 'components/wholePageLoader/PageLoader';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import CommonSubmitBtn from '../../../components/commonInput/CommonSubmitBtn';
import { useColorModeVariables } from '../../../components/colorModeVarialbels/ColorModeValues';
import { RoleOption } from 'utils/StaticData';
import {
  ADMIN_ADDRESS_REQUIRED_FIELDS,
  hasFieldValue,
  userAdminSchema,
} from 'utils/CommonValidation';
import MerchantListModal from '../stationPages/MerchantListModal';
import {
  AddAdminUserData,
  AssignConnectedAccount,
  EditAdminUserData,
  GetActiveConnectedAccounts,
  RemoveConnectedAccount,
  ViewAdminUserData,
} from 'reactRedux/thunks/adminUserThunk/adminUserThunk';
import { revenueOption } from 'utils/StaticData';
import CommonSwitchInput from 'components/commonInput/CommonSwitchInput';
import requestApi from 'utils/Request';
import { showSuccessAlert, showErrorAlert } from 'utils/AlertUtils';
import { City, Country, State } from 'country-state-city';
import { formatPostalCode } from 'utils/PostalCode';

const AdminUserAddEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formatDateForInput = (dateString) => {
    const date = dateString ? new Date(dateString) : new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const { adminEditUserId } = useParams();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { textColor, brandStars } = useColorModeVariables();
  const { adminDetails, loginToken } = useSelector((state) => state.authSlice);
  const {
    isViewLoading,
    viewAdminUserDetails,
    isLoading,
    connectedAccountsList,
    isConnectedAccountsLoading,
    isRemoveConnectedAccountLoading,
  } = useSelector((state) => state.AdminUserSlice);
  // eslint-disable-next-line no-unused-vars
  const [page, setPage] = useState(
    Number(localStorage.getItem('currentPage')) || 1,
  );
  const [isRemove, setIsRemove] = useState(false);
  const [selectedMerchants, setSelectedMerchants] = useState('');
  const [selectedMerchantName, setSelectedMerchantName] = useState();
  const [selectedMerchantObjects, setSelectedMerchantObjects] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(1);

  const handleMerchantsSelected = (newMerchantIds, merchantObjects = []) => {
    const prev = Array.isArray(selectedMerchants) ? selectedMerchants : [];
    const ids = Array.isArray(newMerchantIds) ? newMerchantIds : [];
    const newlyAdded = ids.filter((id) => !prev.includes(id));

    // Snapshot ALL indexed form data keyed by merchantId BEFORE the order
    // changes. This prevents positional index mismatch when the user reorders
    // merchants in the modal (uncheck → recheck in different order).
    const prevDates = getValues('merchantDates') || [];
    const prevCommissions = getValues('merchantCommission') || [];
    const prevCommissionsOn = getValues('merchantCommissionOn') || [];
    const prevIncludePowerbankCost = getValues('includePowerbankCost') || [];
    const prevIncludeCustomerPurchases =
      getValues('includeCustomerPurchases') || [];
    const prevIncludeTransactionCost =
      getValues('includeTransactionCost') || [];
    const prevIsCustomized = getValues('isCustomized') || [];

    const byId = {};
    prev.forEach((id, i) => {
      byId[id] = {
        date: prevDates[i],
        commission: prevCommissions[i],
        commissionOn: prevCommissionsOn[i],
        includePowerbankCost: prevIncludePowerbankCost[i],
        includeCustomerPurchases: prevIncludeCustomerPurchases[i],
        includeTransactionCost: prevIncludeTransactionCost[i],
        isCustomized: prevIsCustomized[i],
      };
    });

    setSelectedMerchants(newMerchantIds);

    const findFirstOrderDate = (id) => {
      const match = merchantObjects.find(
        (m) => Number(m?.merchantId) === Number(id),
      );
      return match?.firstOrderDate || null;
    };

    // Rebuild merchantDates array in the new merchant order
    const updatedDates = ids.map((id) =>
      newlyAdded.includes(id)
        ? formatDateForInput(findFirstOrderDate(id) || new Date())
        : byId[id]?.date || formatDateForInput(new Date()),
    );
    setValue('merchantDates', updatedDates, {
      shouldDirty: newlyAdded.length > 0,
      shouldTouch: newlyAdded.length > 0,
    });

    // Remap every other indexed field to the new merchant order so that
    // existing merchants keep their own data regardless of position change.
    ids.forEach((id, i) => {
      if (newlyAdded.includes(id)) return; // new merchants get defaults via useEffect
      const d = byId[id];
      if (d.commission !== undefined) {
        setValue(`merchantCommission.${i}`, d.commission);
      }
      if (d.commissionOn !== undefined) {
        setValue(`merchantCommissionOn.${i}`, d.commissionOn);
      }
      if (d.includePowerbankCost !== undefined) {
        setValue(`includePowerbankCost.${i}`, d.includePowerbankCost);
      }
      if (d.includeCustomerPurchases !== undefined) {
        setValue(`includeCustomerPurchases.${i}`, d.includeCustomerPurchases);
      }
      if (d.includeTransactionCost !== undefined) {
        setValue(`includeTransactionCost.${i}`, d.includeTransactionCost);
      }
      if (d.isCustomized !== undefined) {
        setValue(`isCustomized.${i}`, d.isCustomized);
      }
    });
  };
  const [regions, setRegions] = useState([]);
  const {
    handleSubmit,
    setValue,
    register,
    control,
    formState: { errors },
    watch,
    getValues,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(
      userAdminSchema(selectedMerchants, !!adminEditUserId, adminDetails.role),
    ),
  });

  // ISO codes backing the cascading address dropdowns (country -> province -> city)
  const [countryIso, setCountryIso] = useState('');
  const [provinceIso, setProvinceIso] = useState('');

  // Cascading address options sourced from the static country-state-city dataset
  const countryOptions = useMemo(() => Country.getAllCountries(), []);
  const provinceOptions = useMemo(
    () => (countryIso ? State.getStatesOfCountry(countryIso) : []),
    [countryIso],
  );
  const cityOptions = useMemo(
    () =>
      countryIso && provinceIso
        ? City.getCitiesOfState(countryIso, provinceIso)
        : [],
    [countryIso, provinceIso],
  );

  const onSubmit = (data) => {
    const merchantDates = data.merchantDates || [];
    const merchantCommissions = data.merchantCommission || [];
    const merchantCommissionsOn = data.merchantCommissionOn || [];
    const includePowerbankCost = data.includePowerbankCost || [];
    const includeCustomerPurchases = data.includeCustomerPurchases || [];
    const includeTransactionCost = data.includeTransactionCost || [];
    const isCustomized = data.isCustomized || [];

    const merchantIds = Array.isArray(data.merchantId)
      ? data.merchantId
      : [data.merchantId].filter(Boolean);

    const merchantPayload = merchantIds.map((merchantId, index) => ({
      merchantId,
      date: merchantDates[index] || null,
      commission: merchantCommissions[index]
        ? Number(merchantCommissions[index])
        : null,
      commissionOn: merchantCommissionsOn[index]?.id || null,
      includePowerbankCost: includePowerbankCost[index] || false,
      includeCustomerPurchases: includeCustomerPurchases[index] || false,
      includeTransactionCost: includeTransactionCost[index] || false,
      isCustomized: isCustomized[index] || false,
    }));

    let payload;
    if (adminEditUserId) {
      payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        ...([4, 6].includes(data.roleId?.id) && {
          merchants: merchantPayload,
          regionId: data.regionId?.id ? data.regionId?.id : +selectedRegion,
          commissionOn: data.commissionOn?.id ? data.commissionOn?.id : 1,
          commission:
            data.commission !== null &&
            data.commission !== undefined &&
            data.commission !== ''
              ? Number(data.commission)
              : 0,
          commissionCap: data.commissionCap ? Number(data.commissionCap) : null,
          bankAccount: data.bankAccount || null,
          financeEmail: data.financeEmail || null,
          addressLine1: data.addressLine1 || null,
          addressLine2: data.addressLine2 || null,
          zipCode: formatPostalCode(data.zipCode, data.country) || null,
          city: data.city || null,
          province: data.province || null,
          country: data.country || null,
        }),
        ...(data.roleId?.id === 6 && {
          organizationName: data.organizationName || null,
          kvk: data.kvk || null,
          vatNumber: data.vatNumber || null,
        }),
        ...(data.roleId?.id === 3 && {
          merchants: merchantPayload,
          regionId: data.regionId?.id ? data.regionId?.id : +selectedRegion,
        }),
      };
    } else {
      payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        roleId: data.roleId?.id,
        merchants: merchantPayload,
        merchantIds: Array.isArray(data.merchantId)
          ? data.merchantId
          : [data.merchantId].filter(Boolean),
        ...((data.roleId?.id === 5 ||
          (([4, 6].includes(data.roleId?.id) || data.roleId?.id === 3) &&
            adminDetails.role === 1)) && {
          regionId: data.regionId?.id ? data.regionId?.id : +selectedRegion,
        }),
        ...([4, 6].includes(data.roleId?.id) && {
          commission:
            data.commission !== null &&
            data.commission !== undefined &&
            data.commission !== ''
              ? Number(data.commission)
              : 0,
          commissionOn: data.commissionOn?.id ? data.commissionOn?.id : 1,
          commissionCap: data.commissionCap ? Number(data.commissionCap) : null,
          bankAccount: data.bankAccount || null,
          financeEmail: data.financeEmail || null,
          addressLine1: data.addressLine1 || null,
          addressLine2: data.addressLine2 || null,
          zipCode: formatPostalCode(data.zipCode, data.country) || null,
          city: data.city || null,
          province: data.province || null,
          country: data.country || null,
        }),
        ...(data.roleId?.id === 6 && {
          organizationName: data.organizationName || null,
          kvk: data.kvk || null,
          vatNumber: data.vatNumber || null,
        }),
      };
    }
    const existingAccount = data.existingConnectedAccount;
    const existingAccountId = existingAccount?.id ?? null;
    const originalAccountId = viewAdminUserDetails?.connectedAccountId ?? null;
    const accountChanged =
      existingAccountId !== null && existingAccountId !== originalAccountId;

    if (adminEditUserId) {
      dispatch(
        EditAdminUserData(adminEditUserId, payload, () => {
          if (accountChanged) {
            dispatch(
              AssignConnectedAccount(adminEditUserId, existingAccountId, () => {
                setPage(Number(localStorage.getItem('currentPage')) || 1);
                navigate('/admin/admin-users');
              }),
            );
          } else {
            setPage(Number(localStorage.getItem('currentPage')) || 1);
            navigate('/admin/admin-users');
          }
        }),
      );
    } else {
      setPage(localStorage.setItem('currentPage', 1) || 1);
      dispatch(
        AddAdminUserData(payload, (createdAdminId) => {
          if (existingAccountId && createdAdminId) {
            dispatch(
              AssignConnectedAccount(createdAdminId, existingAccountId, () => {
                navigate('/admin/admin-users');
              }),
            );
          } else {
            navigate('/admin/admin-users');
          }
        }),
      );
    }
  };

  const handleConnectStripe = async () => {
    const email = getValues('email');
    const name = getValues('firstName');
    const regionId = getValues('regionId')?.id || selectedRegion;
    try {
      const data = await requestApi.post(
        '/admin/stripe/connect',
        {
          email,
          name,
          regionId: Number(regionId),
        },
        {
          headers: {
            Cookie: `token=${loginToken}`,
          },
        },
      );
      showSuccessAlert(data.message);
    } catch (error) {
      showErrorAlert(error.message || 'Error connecting Stripe account');
    }
  };

  useEffect(() => {
    if (adminEditUserId) {
      dispatch(ViewAdminUserData({ id: adminEditUserId }));
    }
  }, [dispatch, adminEditUserId]);

  useEffect(() => {
    if (viewAdminUserDetails && adminEditUserId) {
      setValue('firstName', viewAdminUserDetails?.firstName);
      setValue('lastName', viewAdminUserDetails?.lastName);
      setValue('email', viewAdminUserDetails?.email);
      const selectedRole = RoleOption?.find(
        (role) => role?.id === viewAdminUserDetails?.role,
      );
      setValue('roleId', selectedRole || null);
      if (
        viewAdminUserDetails?.role === 5 ||
        [4, 6].includes(viewAdminUserDetails?.role) ||
        viewAdminUserDetails?.role === 3
      ) {
        const regionId = viewAdminUserDetails?.region?.regionId;
        if (regions.length > 0) {
          const selectedregion = regions?.find(
            (region) => region?.id === regionId,
          );
          setValue('regionId', selectedregion || null);
        }
        setSelectedRegion((prev) => regionId || prev);
      }

      if ([3, 4, 6].includes(viewAdminUserDetails?.role)) {
        const merchantIds =
          viewAdminUserDetails?.merchants?.map((m) => m.merchantId) || [];
        const merchantNames =
          viewAdminUserDetails?.merchants
            ?.map((m) => m.merchantName)
            .join('||') || '';

        setSelectedMerchants(merchantIds);
        setSelectedMerchantName(merchantNames);

        if (
          viewAdminUserDetails?.role === 3 ||
          [4, 6].includes(viewAdminUserDetails?.role)
        ) {
          setValue('merchantId', merchantIds);
        } else {
          setValue('merchantId', merchantIds[0] || '');
        }
        if ([4, 6].includes(viewAdminUserDetails?.role)) {
          const merchantDates = viewAdminUserDetails?.merchants?.map((m) => {
            const date = m.sellerAllocatedDate;
            if (date) return formatDateForInput(date);
            return formatDateForInput(new Date());
          });
          setValue('merchantDates', merchantDates);
        }
      }
      if (
        viewAdminUserDetails?.role === 5 &&
        viewAdminUserDetails?.connectedAccount
      ) {
        setValue(
          'existingConnectedAccount',
          viewAdminUserDetails.connectedAccount,
        );
      }

      if ([4, 6].includes(viewAdminUserDetails?.role)) {
        setValue('commission', viewAdminUserDetails?.commission);
        setValue('commissionCap', viewAdminUserDetails?.commissionCap);
        setValue('bankAccount', viewAdminUserDetails?.bankAccount ?? '');
        setValue('financeEmail', viewAdminUserDetails?.financeEmail ?? '');
        setValue('addressLine1', viewAdminUserDetails?.addressLine1 ?? '');
        setValue('addressLine2', viewAdminUserDetails?.addressLine2 ?? '');
        setValue('zipCode', viewAdminUserDetails?.zipCode ?? '');
        setValue('city', viewAdminUserDetails?.city ?? '');
        setValue('province', viewAdminUserDetails?.province ?? '');
        setValue('country', viewAdminUserDetails?.country ?? '');
        setValue(
          'organizationName',
          viewAdminUserDetails?.organizationName ?? '',
        );
        setValue('kvk', viewAdminUserDetails?.kvk ?? '');
        setValue('vatNumber', viewAdminUserDetails?.vatNumber ?? '');

        // Resolve ISO codes from stored names so the cascading dropdowns can hydrate
        const savedCountry = Country.getAllCountries().find(
          (country) => country.name === viewAdminUserDetails?.country,
        );
        setCountryIso(savedCountry?.isoCode || '');
        if (savedCountry?.isoCode) {
          const savedProvince = State.getStatesOfCountry(
            savedCountry.isoCode,
          ).find(
            (province) => province.name === viewAdminUserDetails?.province,
          );
          setProvinceIso(savedProvince?.isoCode || '');
        } else {
          setProvinceIso('');
        }
        const selectedCommissionOn = revenueOption?.find(
          (commission) => commission?.id === viewAdminUserDetails?.commissionOn,
        );
        setValue('commissionOn', selectedCommissionOn || null);

        if (
          viewAdminUserDetails?.merchants &&
          viewAdminUserDetails.merchants.length > 0
        ) {
          viewAdminUserDetails.merchants.forEach((merchant, index) => {
            let commissionValue = null;
            let commissionOnValue = null;

            if (
              merchant.commission !== null &&
              merchant.commission !== undefined
            ) {
              commissionValue = merchant.commission;
            } else {
              commissionValue = viewAdminUserDetails?.commission;
            }

            if (
              merchant.commissionOn !== null &&
              merchant.commissionOn !== undefined
            ) {
              commissionOnValue = merchant.commissionOn;
            } else {
              commissionOnValue = viewAdminUserDetails?.commissionOn;
            }

            setValue(`merchantCommission.${index}`, commissionValue || '');

            const selectedMerchantCommissionOn = revenueOption?.find(
              (option) => option?.id === commissionOnValue,
            );
            setValue(
              `merchantCommissionOn.${index}`,
              selectedMerchantCommissionOn || null,
            );

            setValue(
              `includePowerbankCost.${index}`,
              merchant.includePowerbankCost ?? false,
            );
            setValue(
              `includeCustomerPurchases.${index}`,
              merchant.includeCustomerPurchases ?? false,
            );
            setValue(
              `includeTransactionCost.${index}`,
              merchant.includeTransactionCost ?? false,
            );
            setValue(`isCustomized.${index}`, merchant.isCustomized ?? false);
          });
        }
      }
    }
  }, [viewAdminUserDetails, adminEditUserId, setValue, regions]);

  const filteredRoleOptions = RoleOption.filter((option) => {
    if ([4, 6].includes(adminDetails?.role)) {
      return option.id === 3;
    } else if (adminDetails?.role === 5) {
      return option.id === 4 || option.id === 3 || option.id === 6;
    }
    return true;
  });

  const handleRemoveMerchant = (indexToRemove) => {
    const roleId = watch('roleId')?.id;

    if ([4, 6].includes(roleId) || roleId === 3) {
      const merchantNamesArray = selectedMerchantName.split('||');
      const updatedMerchantNames = merchantNamesArray.filter(
        (_, index) => index !== indexToRemove,
      );

      const updatedMerchantIds = [...selectedMerchants];
      updatedMerchantIds.splice(indexToRemove, 1);

      const currentMerchantDates = getValues('merchantDates') || [];
      const updatedMerchantDates = currentMerchantDates.filter(
        (_, index) => index !== indexToRemove,
      );
      setValue('merchantDates', updatedMerchantDates);

      const currentIncludePowerbankCost =
        getValues('includePowerbankCost') || [];
      const updatedIncludePowerbankCost = currentIncludePowerbankCost.filter(
        (_, index) => index !== indexToRemove,
      );
      setValue('includePowerbankCost', updatedIncludePowerbankCost);

      const currentIncludeCustomerPurchases =
        getValues('includeCustomerPurchases') || [];
      const updatedIncludeCustomerPurchases =
        currentIncludeCustomerPurchases.filter(
          (_, index) => index !== indexToRemove,
        );
      setValue('includeCustomerPurchases', updatedIncludeCustomerPurchases);

      const currentIncludeTransactionCost =
        getValues('includeTransactionCost') || [];
      const updatedIncludeTransactionCost =
        currentIncludeTransactionCost.filter(
          (_, index) => index !== indexToRemove,
        );
      setValue('includeTransactionCost', updatedIncludeTransactionCost);

      const currentIsCustomized = getValues('isCustomized') || [];
      const updatedIsCustomized = currentIsCustomized.filter(
        (_, index) => index !== indexToRemove,
      );
      setValue('isCustomized', updatedIsCustomized);

      if (updatedMerchantIds.length === 0) {
        setSelectedMerchantName('');
        setSelectedMerchants([]);
        setValue('merchantId', []);
      } else {
        setSelectedMerchantName(updatedMerchantNames.join('||'));
        setSelectedMerchants(updatedMerchantIds);
        setValue('merchantId', updatedMerchantIds);
      }
    } else {
      setSelectedMerchants('');
      setSelectedMerchantName('');
      setIsRemove(true);
      setValue('merchantId', '');
      setValue('merchantDates', []);
    }
  };

  const shouldShowMerchantButton = () => {
    const roleId = watch('roleId')?.id;
    if (![3, 4, 6].includes(roleId)) return false;
    if (Array.isArray(selectedMerchants)) {
      return true;
    }
    return !selectedMerchants || selectedMerchants === '';
  };

  const shouldShowMerchantList = () => {
    const roleId = watch('roleId')?.id;
    if (![3, 4, 6].includes(roleId)) return false;

    if (Array.isArray(selectedMerchants)) {
      return selectedMerchants.length > 0;
    }
    return selectedMerchants && selectedMerchants !== '';
  };

  useEffect(() => {
    if (
      [4, 6].includes(watch('roleId')?.id) &&
      !watch('commissionOn') &&
      !adminEditUserId
    ) {
      const defaultOption = revenueOption.find((opt) => opt.id === 1);
      setValue('commissionOn', defaultOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('roleId'), setValue, adminEditUserId]);

  useEffect(() => {
    if (Array.isArray(selectedMerchants) && selectedMerchants.length > 0) {
      selectedMerchants.forEach((merchantId, index) => {
        const currentIsCustomized = getValues(`isCustomized.${index}`);
        if (typeof currentIsCustomized === 'undefined') {
          setValue(`isCustomized.${index}`, false);
        }

        const parentCommission = getValues('commission');
        const parentCommissionOn =
          getValues('commissionOn') ||
          revenueOption.find((opt) => opt.id === 1);

        const existingMerchantCommissionOn = getValues(
          `merchantCommissionOn.${index}`,
        );
        if (!existingMerchantCommissionOn) {
          setValue(`merchantCommissionOn.${index}`, parentCommissionOn);
        }
        const existingMerchantCommission = getValues(
          `merchantCommission.${index}`,
        );
        if (
          typeof existingMerchantCommission === 'undefined' &&
          parentCommission !== undefined &&
          parentCommission !== null &&
          parentCommission !== ''
        ) {
          setValue(`merchantCommission.${index}`, parentCommission);
        }
        if (selectedMerchantObjects && selectedMerchantObjects.length > 0) {
          const merchant = selectedMerchantObjects.find(
            (obj) => obj.merchantId === merchantId,
          );
          if (merchant) {
            setValue(
              `includePowerbankCost.${index}`,
              merchant.includePowerbankCost ?? false,
            );
            setValue(
              `includeCustomerPurchases.${index}`,
              merchant.includeCustomerPurchases ?? false,
            );
            setValue(
              `includeTransactionCost.${index}`,
              merchant.includeTransactionCost ?? false,
            );
          }
        }
      });
    }
  }, [selectedMerchants, selectedMerchantObjects, setValue, getValues]);

  const watchedCommission = watch('commission');
  const watchedCommissionOn = watch('commissionOn');
  const watchedRoleId = watch('roleId');

  // RAT-1823: the Seller/Organization Address section is saved either
  // completely filled or completely empty. Address Line 2 and Bank Account
  // stay optional and never trigger this.
  const isAddressSectionRequired = watch(ADMIN_ADDRESS_REQUIRED_FIELDS).some(
    hasFieldValue,
  );

  useEffect(() => {
    if (
      [4, 6].includes(watchedRoleId?.id) &&
      Array.isArray(selectedMerchants) &&
      selectedMerchants.length > 0
    ) {
      const parentCommissionOn =
        watchedCommissionOn || revenueOption.find((opt) => opt.id === 1);

      selectedMerchants.forEach((merchantId, index) => {
        const isCust = getValues(`isCustomized.${index}`);
        if (!isCust) {
          if (
            watchedCommission !== undefined &&
            watchedCommission !== null &&
            watchedCommission !== ''
          ) {
            setValue(`merchantCommission.${index}`, watchedCommission);
          }
          setValue(`merchantCommissionOn.${index}`, parentCommissionOn);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedCommission,
    watchedCommissionOn,
    selectedMerchants,
    watchedRoleId,
  ]);

  // Fetch regions from API
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const data = await requestApi.get('/region');
        setRegions(
          data.data?.map((region) => ({
            id: region.regionId,
            label: region.regionName,
            isBajieRegion: region.isBajieRegion ?? false,
          })) || [],
        );
      } catch (_err) {
        // region fetch error handled silently
      }
    };
    fetchRegions();
  }, []);

  // Fetch active connected accounts when dealer role is selected and Super Admin is logged in
  const selectedRoleId = watch('roleId')?.id;
  const selectedRegionId = watch('regionId')?.id;
  useEffect(() => {
    if (adminDetails?.role === 1 && selectedRoleId === 5) {
      dispatch(GetActiveConnectedAccounts());
    }
  }, [adminDetails?.role, selectedRoleId, dispatch]);

  // isBajieRegion=true → region has its own Stripe accounts (NL, Spain, Luxembourg).
  // isBajieRegion=false → region uses NL Stripe as fallback (UK).
  const selectedRegionData = regions.find((r) => r.id === selectedRegionId);
  const mainRegion = regions.find((r) => r.id === 1);
  const effectiveRegionId = selectedRegionData?.isBajieRegion
    ? selectedRegionId
    : (mainRegion?.id ?? 1);
  const filteredConnectedAccounts = selectedRegionId
    ? connectedAccountsList.filter(
        (acc) => acc.regionId === effectiveRegionId || acc.regionId === null,
      )
    : [];

  // Clear connected account selection when region changes (add mode only)
  useEffect(() => {
    if (!adminEditUserId) {
      setValue('existingConnectedAccount', null);
    }
  }, [selectedRegionId, adminEditUserId, setValue]);

  // Fallback pre-fill: when connectedAccountsList loads, match by connectedAccountId
  // (handles case where connectedAccount relation is not populated in view API response)
  useEffect(() => {
    if (
      adminEditUserId &&
      adminDetails?.role === 1 &&
      viewAdminUserDetails?.connectedAccountId &&
      connectedAccountsList.length > 0 &&
      !getValues('existingConnectedAccount')
    ) {
      const match = connectedAccountsList.find(
        (acc) => acc.id === viewAdminUserDetails.connectedAccountId,
      );
      if (match) setValue('existingConnectedAccount', match);
    }
  }, [
    connectedAccountsList,
    viewAdminUserDetails?.connectedAccountId,
    adminEditUserId,
    adminDetails?.role,
    getValues,
    setValue,
  ]);

  // Clear connected account selection when role changes away from dealer
  useEffect(() => {
    if (selectedRoleId !== 5) {
      setValue('existingConnectedAccount', null);
    }
  }, [selectedRoleId, setValue]);

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <Flex direction="column" align="center" justify="center">
          <Card
            w="100%"
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            borderRadius="20px"
            paddingInline="40px"
            paddingBlock="50px"
          >
            {isViewLoading ? (
              <PageLoader />
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <FormControl>
                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 4 }}
                    spacing="24px"
                  >
                    {/* First Name */}
                    <CommonInput
                      type="text"
                      name="firstName"
                      labelText="First Name"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="First Name"
                      register={register('firstName')}
                      errors={errors?.firstName}
                      isValidate={true}
                    />

                    {/* Last Name */}
                    <CommonInput
                      type="text"
                      name="lastName"
                      labelText="Last Name"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Last Name"
                      register={register('lastName')}
                      errors={errors?.lastName}
                      isValidate={true}
                    />

                    {/* Email */}
                    <CommonInput
                      type="email"
                      name="email"
                      labelText="Contact email"
                      textColor={textColor}
                      brandStars={brandStars}
                      placeholder="Email"
                      register={register('email')}
                      errors={errors?.email}
                      isValidate={true}
                    />

                    {/* Role Option select */}
                    <Controller
                      name="roleId"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Role"
                          name="roleId"
                          type="select"
                          options={filteredRoleOptions}
                          value={value ? [value] : []}
                          onChange={(selectedRole) => {
                            const newValue = Array.isArray(selectedRole)
                              ? selectedRole[0]
                              : selectedRole;
                            onChange(newValue);
                            setSelectedMerchants('');
                            setSelectedMerchantName('');
                            setValue('merchantId', '');
                            if (
                              value?.id === 5 &&
                              newValue?.id !== 5 &&
                              !adminEditUserId
                            ) {
                              setValue('regionId', '');
                            }
                          }}
                          register={register}
                          isRequired={true}
                          error={errors?.roleId}
                          getOptionLabel={(e) => e?.label}
                          getOptionValue={(e) => e?.id}
                          isDisabled={!!adminEditUserId}
                        />
                      )}
                    />

                    {/* region selection for role option is dealer */}
                    {watch('roleId')?.id === 5 && (
                      <Controller
                        name="regionId"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CommonReactSelect
                            label="Region"
                            name="regionId"
                            type="select"
                            options={regions}
                            value={value}
                            onChange={onChange}
                            register={register}
                            isRequired={true}
                            error={errors?.regionId}
                            getOptionLabel={(e) => e?.label}
                            getOptionValue={(e) => e?.id}
                            isDisabled={!!adminEditUserId}
                          />
                        )}
                      />
                    )}
                  </SimpleGrid>

                  {/* Address Section for seller role */}
                  {[4, 6].includes(watch('roleId')?.id) && (
                    <Box mt="50px">
                      <Box
                        fontSize="xl"
                        fontWeight="bold"
                        color="gray.800"
                        mb={6}
                        pb={2}
                        borderBottom="2px solid"
                        borderColor="primary"
                      >
                        ADDRESS
                      </Box>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 4 }}
                        spacing="24px"
                      >
                        {/* Address Line 1 */}
                        <CommonInput
                          type="text"
                          name="addressLine1"
                          labelText="Address Line 1"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Enter Address Line 1"
                          register={register('addressLine1')}
                          errors={errors?.addressLine1}
                          isValidate={isAddressSectionRequired}
                        />

                        {/* Address Line 2 */}
                        <CommonInput
                          type="text"
                          name="addressLine2"
                          labelText="Address Line 2"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Enter Address Line 2"
                          register={register('addressLine2')}
                          errors={errors?.addressLine2}
                          isValidate={false}
                        />

                        {/* Zip Code */}
                        <CommonInput
                          type="text"
                          name="zipCode"
                          labelText="Zip Code"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Enter Zip Code"
                          register={register('zipCode')}
                          errors={errors?.zipCode}
                          isValidate={isAddressSectionRequired}
                          onBlur={(e) =>
                            setValue(
                              'zipCode',
                              formatPostalCode(
                                e.target.value,
                                watch('country'),
                              ),
                              { shouldValidate: true },
                            )
                          }
                        />

                        {/* Country */}
                        <Controller
                          name="country"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <CommonReactSelect
                              label="Country"
                              name="country"
                              type="select"
                              options={countryOptions}
                              value={
                                countryOptions.find((c) => c.name === value) ||
                                null
                              }
                              onChange={(option) => {
                                onChange(option?.name || '');
                                setCountryIso(option?.isoCode || '');
                                // Reset dependent fields when the country changes
                                setProvinceIso('');
                                setValue('province', '');
                                setValue('city', '');
                              }}
                              register={register}
                              isRequired={isAddressSectionRequired}
                              error={errors?.country}
                              getOptionLabel={(e) => e?.name}
                              getOptionValue={(e) => e?.isoCode}
                            />
                          )}
                        />

                        {/* Province */}
                        <Controller
                          name="province"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <CommonReactSelect
                              label="Province"
                              name="province"
                              type="select"
                              options={provinceOptions}
                              value={
                                provinceOptions.find((s) => s.name === value) ||
                                null
                              }
                              onChange={(option) => {
                                onChange(option?.name || '');
                                setProvinceIso(option?.isoCode || '');
                                // Reset city when the province changes
                                setValue('city', '');
                              }}
                              register={register}
                              isRequired={isAddressSectionRequired}
                              error={errors?.province}
                              getOptionLabel={(e) => e?.name}
                              getOptionValue={(e) => e?.isoCode}
                              isDisabled={!countryIso}
                            />
                          )}
                        />

                        {/* City */}
                        <Controller
                          name="city"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <CommonReactSelect
                              label="City"
                              name="city"
                              type="select"
                              options={cityOptions}
                              value={
                                cityOptions.find((c) => c.name === value) ||
                                null
                              }
                              onChange={(option) =>
                                onChange(option?.name || '')
                              }
                              register={register}
                              isRequired={isAddressSectionRequired}
                              error={errors?.city}
                              getOptionLabel={(e) => e?.name}
                              getOptionValue={(e) => e?.name}
                              isDisabled={!provinceIso}
                            />
                          )}
                        />

                        {/* Legal fields (Organization Name + KvK + VAT Number) for organization role */}
                        {watch('roleId')?.id === 6 && (
                          <>
                            <CommonInput
                              type="text"
                              name="organizationName"
                              labelText="Organization Name"
                              textColor={textColor}
                              brandStars={brandStars}
                              placeholder="Enter Organization Name"
                              register={register('organizationName')}
                              errors={errors?.organizationName}
                              isValidate={true}
                            />

                            <CommonInput
                              type="text"
                              name="kvk"
                              labelText="KvK"
                              textColor={textColor}
                              brandStars={brandStars}
                              placeholder="Enter KvK"
                              register={register('kvk')}
                              errors={errors?.kvk}
                              isValidate={false}
                            />

                            <CommonInput
                              type="text"
                              name="vatNumber"
                              labelText="VAT Number"
                              textColor={textColor}
                              brandStars={brandStars}
                              placeholder="Enter VAT Number"
                              register={register('vatNumber')}
                              errors={errors?.vatNumber}
                              isValidate={false}
                            />
                          </>
                        )}
                      </SimpleGrid>
                    </Box>
                  )}

                  {/* Commission Section for seller role */}
                  {[4, 6].includes(watch('roleId')?.id) && (
                    <Box mt="50px">
                      <Box
                        fontSize="xl"
                        fontWeight="bold"
                        color="gray.800"
                        mb={6}
                        pb={2}
                        borderBottom="2px solid"
                        borderColor="primary"
                      >
                        FINANCE
                      </Box>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 4 }}
                        spacing="24px"
                      >
                        {/* Commission On */}
                        <Controller
                          name="commissionOn"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <CommonReactSelect
                              label="Commission On"
                              name="commissionOn"
                              type="select"
                              options={revenueOption}
                              value={value}
                              onChange={onChange}
                              register={register}
                              isRequired={true}
                              error={errors?.commissionOn}
                              getOptionLabel={(e) => e?.label}
                              getOptionValue={(e) => e?.id}
                            />
                          )}
                        />

                        {/* Commission */}
                        <CommonInput
                          type="number"
                          name="commission"
                          labelText="Commission"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Commission"
                          register={register('commission')}
                          errors={errors?.commission}
                          isValidate={false}
                          isAddPercentage={true}
                        />

                        {/* Commission Cap */}
                        <CommonInput
                          type="number"
                          name="commissionCap"
                          labelText="Commission Cap"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Commission Cap"
                          register={register('commissionCap')}
                          errors={errors?.commissionCap}
                          isValidate={false}
                        />

                        {/* Bank Account */}
                        <CommonInput
                          type="text"
                          name="bankAccount"
                          labelText="Bank Account"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Bank Account"
                          register={register('bankAccount')}
                          errors={errors?.bankAccount}
                          isValidate={false}
                        />

                        {/* Finance Email */}
                        <CommonInput
                          type="email"
                          name="financeEmail"
                          labelText="Finance email"
                          textColor={textColor}
                          brandStars={brandStars}
                          placeholder="Finance email"
                          register={register('financeEmail')}
                          errors={errors?.financeEmail}
                          isValidate={false}
                        />
                      </SimpleGrid>
                    </Box>
                  )}

                  {/* Merchant Selection Section */}
                  {shouldShowMerchantButton() && (
                    <>
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 3 }}
                        spacing="24px"
                      >
                        <Button
                          type="button"
                          bg="reviveOrange"
                          mt="50px"
                          h="50"
                          color="primary"
                          borderRadius="12px"
                          _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                          onClick={onOpen}
                        >
                          <Icon
                            as={FaPlus}
                            width="15px"
                            height="15px"
                            borderRadius="12px"
                            marginRight="5px"
                            color="inherit"
                          />
                          Add Merchant
                        </Button>
                      </SimpleGrid>
                      {errors.merchantId && (
                        <p style={{ color: 'red', marginTop: '5px' }}>
                          {errors.merchantId.message}
                        </p>
                      )}
                    </>
                  )}

                  {shouldShowMerchantList() && (
                    <Box mt="50px">
                      <FormLabel
                        color={textColor}
                        fontSize="lg"
                        fontWeight="semibold"
                        mb={2}
                      >
                        Merchants
                      </FormLabel>

                      <Flex
                        flexWrap="wrap"
                        gap={
                          [4, 6].includes(watch('roleId')?.id) ? '32px' : '0px'
                        }
                      >
                        {(Array.isArray(selectedMerchants)
                          ? selectedMerchantName
                              .split('||')
                              .filter((name) => name.trim() !== '')
                          : [selectedMerchantName].filter(
                              (name) => name && name.trim() !== '',
                            )
                        ).map((name, index) => (
                          <Box
                            key={index}
                            width="100%"
                            mb={[4, 6].includes(watch('roleId')?.id) ? 4 : 0}
                          >
                            {/* Merchant Name and Close button row */}
                            <Flex
                              alignItems="center"
                              justifyContent="space-between"
                              mb={[4, 6].includes(watch('roleId')?.id) ? 2 : 0}
                              gap={3}
                            >
                              {/* Merchant Name */}
                              <FormLabel
                                color={textColor}
                                fontSize="md"
                                mb={0}
                                flex="1"
                              >
                                {name.trim()}
                              </FormLabel>

                              {/* Desktop: Is Customized checkbox inline */}
                              {[4, 6].includes(watch('roleId')?.id) && (
                                <Box
                                  display={{ base: 'none', lg: 'block' }}
                                  mt={0}
                                  sx={{
                                    '& > div': {
                                      mt: '0 !important',
                                    },
                                  }}
                                >
                                  <CommonSwitchInput
                                    labelText="Is Customized"
                                    name={`isCustomized.${index}`}
                                    errors={null}
                                    isValidate={false}
                                    textColor="gray.700"
                                    brandStars="red.500"
                                    control={control}
                                  />
                                </Box>
                              )}

                              {/* Close button */}
                              {(!adminEditUserId ||
                                (adminEditUserId &&
                                  [4, 6].includes(watch('roleId')?.id)) ||
                                watch('roleId')?.id === 3) && (
                                <IconButton
                                  aria-label={`Remove ${name}`}
                                  icon={<CloseIcon />}
                                  size="sm"
                                  colorScheme="red"
                                  onClick={() => handleRemoveMerchant(index)}
                                  variant="ghost"
                                />
                              )}
                            </Flex>

                            {/* Mobile/Tablet: Is Customized checkbox below merchant name */}
                            {[4, 6].includes(watch('roleId')?.id) && (
                              <Box
                                display={{ base: 'block', lg: 'none' }}
                                mb={3}
                                mt={0}
                                sx={{
                                  '& > div': {
                                    mt: '0 !important',
                                  },
                                }}
                              >
                                <CommonSwitchInput
                                  labelText="Is Customized"
                                  name={`isCustomized.${index}`}
                                  errors={null}
                                  isValidate={false}
                                  textColor="gray.700"
                                  brandStars="red.500"
                                  control={control}
                                />
                              </Box>
                            )}

                            {/* Fields row */}
                            {[4, 6].includes(watch('roleId')?.id) && (
                              <SimpleGrid
                                columns={{ base: 1, md: 1, lg: 3 }}
                                columnGap={5}
                                rowGap={{ base: 3, md: 3, lg: 0 }}
                                mb={4}
                              >
                                <>
                                  <Box w={'100%'}>
                                    <Controller
                                      name={`merchantDates.${index}`}
                                      control={control}
                                      render={({
                                        field: { onChange, value },
                                      }) => (
                                        <CommonDatePicker
                                          labelText="Allocated Date"
                                          textColor={textColor}
                                          brandStars={brandStars}
                                          value={value}
                                          onChange={onChange}
                                          errors={
                                            errors?.merchantDates?.[index]
                                          }
                                          isRequired={false}
                                        />
                                      )}
                                    />
                                  </Box>

                                  <Box w={'100%'}>
                                    <Controller
                                      name={`merchantCommissionOn.${index}`}
                                      control={control}
                                      render={({
                                        field: { onChange, value },
                                      }) => (
                                        <CommonReactSelect
                                          label="Commission On"
                                          name={`merchantCommissionOn.${index}`}
                                          type="select"
                                          options={revenueOption}
                                          value={value}
                                          onChange={onChange}
                                          register={register}
                                          isRequired={false}
                                          error={
                                            errors?.merchantCommissionOn?.[
                                              index
                                            ]
                                          }
                                          getOptionLabel={(e) => e?.label}
                                          getOptionValue={(e) => e?.id}
                                          placeholderValue="Select option"
                                          isDisabled={
                                            !watch(`isCustomized.${index}`)
                                          }
                                        />
                                      )}
                                    />
                                  </Box>

                                  <Box w={'100%'}>
                                    <CommonInput
                                      type="number"
                                      name={`merchantCommission.${index}`}
                                      labelText="Commission"
                                      textColor={textColor}
                                      brandStars={brandStars}
                                      placeholder="Commission"
                                      register={register(
                                        `merchantCommission.${index}`,
                                      )}
                                      errors={
                                        errors?.merchantCommission?.[index]
                                      }
                                      isValidate={false}
                                      isAddPercentage={true}
                                      isDisabled={
                                        !watch(`isCustomized.${index}`)
                                      }
                                    />
                                  </Box>

                                  <Box w={'100%'}>
                                    <CommonSwitchInput
                                      labelText="Deduct costs of powerbanks lost"
                                      name={`includePowerbankCost.${index}`}
                                      register={register(
                                        `includePowerbankCost.${index}`,
                                      )}
                                      errors={
                                        errors?.includePowerbankCost?.[index]
                                      }
                                      isValidate={false}
                                      defaultChecked={false}
                                      textColor="gray.700"
                                      brandStars="red.500"
                                      control={control}
                                      isDisabled={
                                        !watch(`isCustomized.${index}`)
                                      }
                                    />
                                  </Box>

                                  <Box w={'100%'}>
                                    <CommonSwitchInput
                                      labelText="Deduct costs of purchased powerbanks"
                                      name={`includeCustomerPurchases.${index}`}
                                      register={register(
                                        `includeCustomerPurchases.${index}`,
                                      )}
                                      errors={
                                        errors?.includeCustomerPurchases?.[
                                          index
                                        ]
                                      }
                                      isValidate={false}
                                      defaultChecked={false}
                                      textColor="gray.700"
                                      brandStars="red.500"
                                      control={control}
                                      isDisabled={
                                        !watch(`isCustomized.${index}`)
                                      }
                                    />
                                  </Box>

                                  <Box w={'100%'}>
                                    <CommonSwitchInput
                                      labelText="Deduct Transaction Costs?"
                                      name={`includeTransactionCost.${index}`}
                                      register={register(
                                        `includeTransactionCost.${index}`,
                                      )}
                                      errors={
                                        errors?.includeTransactionCost?.[index]
                                      }
                                      isValidate={false}
                                      defaultChecked={false}
                                      textColor="gray.700"
                                      brandStars="red.500"
                                      control={control}
                                      isDisabled={
                                        !watch(`isCustomized.${index}`)
                                      }
                                    />
                                  </Box>
                                </>
                              </SimpleGrid>
                            )}
                          </Box>
                        ))}
                      </Flex>
                    </Box>
                  )}

                  {/* Stripe Connected Account — dealer role, Super Admin only */}
                  {adminDetails?.role === 1 && watch('roleId')?.id === 5 && (
                    <Box mt="50px">
                      <Box
                        fontSize="xl"
                        fontWeight="bold"
                        color="gray.800"
                        mb={6}
                        pb={2}
                        borderBottom="2px solid"
                        borderColor="primary"
                      >
                        STRIPE ACCOUNT
                      </Box>

                      <Flex
                        gap="16px"
                        alignItems="flex-end"
                        flexWrap="wrap"
                        maxW="700px"
                      >
                        <Box flex="1" minW="240px">
                          <Controller
                            name="existingConnectedAccount"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <CommonReactSelect
                                label="Select Connected Account"
                                name="existingConnectedAccount"
                                type="select"
                                options={filteredConnectedAccounts}
                                value={value}
                                onChange={onChange}
                                register={register}
                                isRequired={false}
                                error={errors?.existingConnectedAccount}
                                getOptionLabel={(e) =>
                                  `${e?.name}  (${e?.stripeAccountId ?? ''})`
                                }
                                getOptionValue={(e) => e?.id}
                                isLoading={isConnectedAccountsLoading}
                                textColor={textColor}
                                brandStars={brandStars}
                              />
                            )}
                          />
                        </Box>
                        <Button
                          type="button"
                          bg="reviveOrange"
                          mb="4px"
                          h="46px"
                          color="primary"
                          borderRadius="12px"
                          fontSize="sm"
                          flexShrink={0}
                          _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                          onClick={handleConnectStripe}
                        >
                          + Connect New Account
                        </Button>
                      </Flex>

                      {/* Account details card — shown when an account is selected */}
                      {watch('existingConnectedAccount') && (
                        <Box
                          mt={4}
                          p={4}
                          bg="gray.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.200"
                          maxW="700px"
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            mb={3}
                            flexWrap="wrap"
                            gap={2}
                          >
                            <Box
                              fontSize="sm"
                              fontWeight="semibold"
                              color="gray.600"
                            >
                              Account Details
                            </Box>
                            {adminEditUserId &&
                              viewAdminUserDetails?.connectedAccountId && (
                                <Button
                                  type="button"
                                  size="xs"
                                  colorScheme="red"
                                  variant="ghost"
                                  isLoading={isRemoveConnectedAccountLoading}
                                  onClick={() => {
                                    dispatch(
                                      RemoveConnectedAccount(
                                        adminEditUserId,
                                        () => {
                                          setValue(
                                            'existingConnectedAccount',
                                            null,
                                          );
                                          dispatch(
                                            ViewAdminUserData({
                                              id: adminEditUserId,
                                            }),
                                          );
                                        },
                                      ),
                                    );
                                  }}
                                >
                                  Remove Account
                                </Button>
                              )}
                          </Flex>
                          <SimpleGrid
                            columns={{ base: 1, md: 2, lg: 4 }}
                            spacing={3}
                          >
                            {[
                              {
                                label: 'Account Name',
                                value:
                                  watch('existingConnectedAccount')?.name ??
                                  'N/A',
                                truncate: true,
                              },
                              {
                                label: 'Stripe ID',
                                value: (() => {
                                  const sid = watch(
                                    'existingConnectedAccount',
                                  )?.stripeAccountId;
                                  if (!sid) return 'N/A';
                                  return sid.length > 14
                                    ? `${sid.slice(0, 8)}...${sid.slice(-6)}`
                                    : sid;
                                })(),
                                truncate: true,
                              },
                              {
                                label: 'Country',
                                value:
                                  watch('existingConnectedAccount')?.country ??
                                  'N/A',
                              },
                              {
                                label: 'Status',
                                value: watch('existingConnectedAccount')
                                  ?.isActive
                                  ? '✓ Active'
                                  : '✗ Inactive',
                              },
                            ].map(({ label, value, truncate, mono }) => (
                              <Flex
                                key={label}
                                direction="column"
                                gap="2px"
                                padding="6px 8px"
                                borderLeft="2px solid"
                                borderColor="gray.300"
                                minW={0}
                              >
                                <Box fontSize="xs" color="gray.500">
                                  {label}
                                </Box>
                                <Box
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="gray.700"
                                  overflow={truncate ? 'hidden' : undefined}
                                  textOverflow={
                                    truncate ? 'ellipsis' : undefined
                                  }
                                  whiteSpace={truncate ? 'nowrap' : undefined}
                                  wordBreak={mono ? 'break-all' : undefined}
                                  title={truncate ? value : undefined}
                                  fontFamily={mono ? 'mono' : undefined}
                                >
                                  {value}
                                </Box>
                              </Flex>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )}
                    </Box>
                  )}

                  <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3 }}
                    spacing="24px"
                    mt="26px"
                  >
                    <CommonSubmitBtn BTNtext="Submit" isLoading={isLoading} />
                  </SimpleGrid>

                  {[3, 4, 6].includes(watch('roleId')?.id) && (
                    <MerchantListModal
                      isOpen={isOpen}
                      onClose={onClose}
                      setSelectedMerchants={handleMerchantsSelected}
                      setSelectedMerchantName={setSelectedMerchantName}
                      setSelectedMerchantObjects={setSelectedMerchantObjects}
                      setValue={setValue}
                      roleId={watch('roleId')?.id}
                      setIsRemove={setIsRemove}
                      isRemove={isRemove}
                      currentSelectedMerchants={
                        Array.isArray(selectedMerchants)
                          ? selectedMerchants
                          : []
                      }
                      currentSelectedMerchantNames={selectedMerchantName}
                      selectedRegion={selectedRegion}
                      setSelectedRegion={setSelectedRegion}
                      sellerId={adminEditUserId}
                      adminDetails={adminDetails}
                      includeFirstOrderDate={true}
                    />
                  )}
                </FormControl>
              </form>
            )}
          </Card>
        </Flex>
      </Box>
    </>
  );
};

export default AdminUserAddEdit;
